import { useMutation, useQuery } from 'react-query';

import { http } from '../http';

import {
  TaskSolutionIssue,
  CompleteTaskBody,
  TaskDetails,
  TaskInfo,
} from '../__generated__/api-di';
import { queryClient } from '../config/react-query';
import env from '../config/env';

interface Id {
  commercialLabel: string;
  loanNumber: number;
  index: number;
}

interface DetailId extends Id {
  taskId: string;
}

async function getTasks(id: Id): Promise<TaskInfo[]> {
  if (!env.FEATURES.TASKS) {
    return [];
  }

  const { loanNumber, index, commercialLabel } = id ?? {};
  const response = await http.get<TaskInfo[]>(
    `/api/v1/lenders/${commercialLabel}/cases/${loanNumber}-${index}/tasks`
  );
  return response.data;
}

async function completeTask(id: DetailId, completeTaskBody?: CompleteTaskBody): Promise<any> {
  const { loanNumber, index, commercialLabel, taskId } = id ?? {};
  await http.post(
    `/api/v1/lenders/${commercialLabel}/cases/${loanNumber}-${index}/tasks/${taskId}/complete`,
    JSON.stringify(completeTaskBody)
  );
}

const tasks = 'tasks';
export function useQueryTasks(id: Id) {
  return useQuery([tasks, id], () => getTasks(id));
}

async function getTaskDetails(id: DetailId): Promise<TaskDetails> {
  const { loanNumber, index, taskId, commercialLabel } = id ?? {};
  const response = await http.get<TaskDetails>(
    `/api/v1/lenders/${commercialLabel}/cases/${loanNumber}-${index}/tasks/${taskId}`
  );
  return response.data;
}

const taskDetails = 'taskDetails';
export function useQueryTaskDetails(id?: DetailId) {
  return useQuery([taskDetails, id], () => getTaskDetails(id!), {
    enabled: !!id,
  });
}

export function useSubmitIssues(succesCallback: () => void, errorCallback: () => void) {
  return useMutation(
    (variables: { id?: DetailId; issues: Array<TaskSolutionIssue>; comment?: string }) =>
      completeTask(variables.id!, {
        data: {
          issues: variables.issues,
          comment: variables.comment,
        },
      }),
    {
      onSuccess: (data, variables) => {
        const { taskId, ...nonDetailId } = variables.id!;
        const queryKey = [tasks, nonDetailId];
        const queryKeyDetails = [taskDetails, variables.id];
        // Does this need a timeout?
        queryClient.invalidateQueries(queryKey);
        queryClient.invalidateQueries(queryKeyDetails);
        succesCallback();
      },
      onError: () => {
        errorCallback();
      },
    }
  );
}
